import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, ShieldCheck, Lock, Loader2, Eye, EyeOff } from "lucide-react";
import { LOGO } from "../../data/content";
import { NetworkCanvas } from "../../components/NetworkCanvas";
import { FadeIn, MaskedLine } from "../../components/motion/Reveal";
import { MailApp } from "../../components/mail/MailApp";
import { ForcePasswordModal } from "../../components/mail/ForcePasswordModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API, withCredentials: true });

export default function Webmail() {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const [mustChange, setMustChange] = useState(null); // email string when a temporary password must be replaced
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/mail/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/mail/login", { email, password });
      if (data.must_change_password) {
        setMustChange(data.email);
      } else {
        setUser(data);
        toast.success(`Welcome — signed in as ${data.email}`);
      }
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Sign in failed — try again");
    } finally {
      setBusy(false);
    }
  };

  const logout = async () => {
    try {
      await api.post("/mail/logout");
    } catch { /* session cleared regardless */ }
    setUser(false);
    setPassword("");
  };

  if (user === null) {
    return <div className="min-h-screen bg-nx-navy" />;
  }
  if (mustChange) {
    return (
      <div className="min-h-screen bg-nx-navy">
        <ForcePasswordModal
          api={api}
          email={mustChange}
          onDone={() => {
            const em = mustChange;
            setMustChange(null);
            setUser({ email: em });
          }}
        />
      </div>
    );
  }
  if (user) {
    return <MailApp api={api} user={user} onLogout={logout} />;
  }

  return (
    <div className="font-publicsans bg-nx-navy text-slate-200 antialiased min-h-screen flex flex-col relative overflow-hidden" data-testid="webmail-page">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,#10203a_0%,#0a1526_60%,#060d1a_100%)]" />
      <NetworkCanvas skyline={false} className="absolute inset-0 w-full h-full opacity-40" />
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_30%,rgba(6,13,26,0.85)_100%)]" />

      <header className="relative z-10 nx-wrap pt-7">
        <a href="/" aria-label="NexSecure home"><img src={LOGO.fullDark} alt="NexSecure Technologies" className="h-9 w-auto" data-testid="webmail-logo" /></a>
      </header>

      <main className="relative z-10 flex-1 flex items-center justify-center px-5 py-16">
        <div className="w-full max-w-4xl grid lg:grid-cols-2 gap-10 items-center">
          <div className="hidden lg:block">
            <FadeIn delay={0.1}>
              <div className="flex items-center gap-4 mb-5">
                <span className="h-px w-12 bg-nx-gold/70" />
                <span className="text-[11px] tracking-[0.3em] uppercase text-nx-goldlight">Staff Access</span>
              </div>
            </FadeIn>
            <h1 className="font-marcellus text-5xl text-white leading-[1.08]" data-testid="webmail-title">
              <MaskedLine delay={0.2}>NexSecure</MaskedLine>
              <MaskedLine delay={0.35} innerClassName="text-nx-goldlight">Mail</MaskedLine>
            </h1>
            <FadeIn delay={0.5}>
              <p className="mt-6 text-base text-slate-400 leading-relaxed max-w-md">
                Your @nexsecure.in mailbox — proposals, client conversations and team updates. One sign-in, straight to your inbox.
              </p>
              <div className="mt-8 flex flex-col gap-3 text-sm text-slate-400">
                <span className="flex items-center gap-3"><ShieldCheck size={16} className="text-nx-goldlight shrink-0" /> Encrypted connection, always</span>
                <span className="flex items-center gap-3"><Lock size={16} className="text-nx-goldlight shrink-0" /> Never share your password — NexSecure IT will never ask for it</span>
              </div>
            </FadeIn>
          </div>

          <FadeIn delay={0.3}>
            <div className="border border-white/10 bg-nx-navy2/80 backdrop-blur-md p-8 sm:p-10" data-testid="webmail-card">
              <div className="flex items-center gap-3 mb-2">
                <span className="w-10 h-10 flex items-center justify-center border border-nx-gold/40 bg-nx-gold/10"><Mail size={18} className="text-nx-goldlight" /></span>
                <p className="text-[10px] tracking-[0.24em] uppercase text-slate-500">Team sign in</p>
              </div>
              <h2 className="font-marcellus text-2xl text-white mb-6">Open your inbox</h2>
              <form onSubmit={login} className="flex flex-col gap-4" autoComplete="off">
                <div>
                  <label htmlFor="webmail-email" className="block text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-2">Work email</label>
                  <input
                    id="webmail-email" type="email" required autoComplete="off" data-lpignore="true" data-1p-ignore="true" data-bwignore="true"
                    placeholder="you@nexsecure.in"
                    className="w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-4 py-3.5 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors"
                    value={email} onChange={(e) => setEmail(e.target.value)}
                    data-testid="webmail-email-input"
                  />
                </div>
                <div>
                  <label htmlFor="webmail-password" className="block text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-2">Password</label>
                  <div className="relative">
                    <input
                      id="webmail-password" type={showPass ? "text" : "password"} required autoComplete="new-password" data-lpignore="true" data-1p-ignore="true" data-bwignore="true"
                      placeholder="Your mailbox password"
                      className="w-full bg-white/[0.04] border border-white/15 text-slate-100 placeholder:text-slate-500 px-4 py-3.5 pr-12 text-sm focus:outline-none focus:border-nx-goldlight/70 transition-colors"
                      value={password} onChange={(e) => setPassword(e.target.value)}
                      data-testid="webmail-password-input"
                    />
                    <button
                      type="button" onClick={() => setShowPass(!showPass)}
                      className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                      aria-label={showPass ? "Hide password" : "Show password"}
                      data-testid="webmail-show-password"
                    >
                      {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <button
                  type="submit" disabled={busy}
                  className="flex items-center justify-center gap-2 bg-nx-gold hover:bg-nx-goldlight text-nx-navy font-semibold tracking-wide text-sm px-8 py-4 transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60 disabled:hover:translate-y-0"
                  data-testid="webmail-login-button"
                >
                  {busy ? <Loader2 size={16} className="animate-spin" /> : null}
                  {busy ? "Signing in…" : "Sign In to Mail"}
                </button>
                <p className="text-[11px] text-slate-600 leading-relaxed">
                  Your password is verified directly against the NexSecure mail server over an encrypted connection. Trouble signing in? Contact your IT administrator.
                </p>
              </form>
            </div>
          </FadeIn>
        </div>
      </main>

      <footer className="relative z-10 nx-wrap pb-6">
        <p className="text-[11px] text-slate-600">© {new Date().getFullYear()} NexSecure Technologies · <a href="/" className="hover:text-slate-400 transition-colors">Back to website</a></p>
      </footer>
    </div>
  );
}
